import React from 'react';
import { trackEvent } from './utils/analytics';
import { useSaleOffer, SALE_OFFER, fmtUsd } from './hooks/useSaleOffer';
import Reviews from './Reviews';
import VideoEmbed from './components/VideoEmbed';
import {
  BookOpenText, Heart, Gift, Feather, Microphone, Sparkle, Confetti,
  ShieldCheck, Lock, MapPin, CheckCircle,
  List as MenuIcon, X, VideoCamera, QrCode, DeviceMobile
} from '@phosphor-icons/react';

/*
 * LandingPageV2 — Redesigned landing page using Talebound AAA Design Spec.
 * Fonts: Playfair Display + Inter
 * Colors: Ink Navy #0f172a, Parchment #fbf8f1, Deep Amber Brown #7c2d12
 * Preview at: /landing-v2
 */

// Load fonts: Cinzel for display headings, Lora for body
if (!document.getElementById('talebound-v2-fonts')) {
  const link = document.createElement('link');
  link.id = 'talebound-v2-fonts';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&display=swap';
  document.head.appendChild(link);
}

// ── DESIGN TOKENS ──
const C = {
  parchment: '#fbf8f1',
  ink: '#0f172a',
  tan: '#efebe0',
  amber: '#7c2d12',       // deep amber/burgundy — 8.8:1 on parchment (AAA)
  amberDark: '#6b211a',
  amberWarm: '#92400e',   // mid amber — 4.93:1 on parchment (AA) — use for gift CTAs
  amberDecor: '#b85019',  // burnt orange — 4.7:1 white-on-button (AA) — safe on light AND dark
  gold: '#c9a84c',        // gold — accent sparingly
  muted: '#3c4653',       // 9:1 on parchment (AAA)
  border: '#dcd8ce',
  white: '#ffffff',
  memoryCare: '#f8f5ef',
};
const ink = (a) => `rgba(15,23,42,${a})`;
const F = {
  heading: "'Lora', Georgia, serif",     // display headings + brand name — Lora
  body: "'Lora', Georgia, serif",        // all body copy — Lora
};
const R = { pill: '9999px', xl: 16, lg: 12, md: 8, sm: 6 };
const SH = {
  sm: '0 1px 3px rgba(0,0,0,0.06)',
  btn: '0 1px 3px rgba(0,0,0,0.12)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 24px rgba(0,0,0,0.10)',
  xl: '0 16px 48px rgba(0,0,0,0.12)',
  xxl: '0 24px 64px rgba(0,0,0,0.16)',
};

// ── EMBEDDED STYLES ──
const CSS = `
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
.lpv2{background:${C.parchment};color:${C.ink};font-family:${F.body};overflow-x:hidden;min-height:100vh;font-size:18px;line-height:1.7}
.lpv2 h1,.lpv2 h2,.lpv2 h3,.lpv2 h4{font-family:${F.heading};margin:0}

/* ── NAV ── */
/* ── GIFT BAR (TB-087) ── */
.v2-giftbar{position:fixed;top:0;left:0;right:0;height:44px;z-index:101;display:flex;align-items:center;justify-content:center;gap:10px;background:linear-gradient(90deg,#6e4322,#553016);color:${C.parchment};font-family:${F.body};font-size:15px;font-weight:600;letter-spacing:0.3px;cursor:pointer;border:none;width:100%;padding:0 16px;text-decoration:none;white-space:nowrap;overflow:hidden;transition:filter 150ms ease}
.v2-giftbar:hover{filter:brightness(1.08)}
.v2-giftbar-arrow{display:inline-block;transition:transform 150ms ease}
.v2-giftbar:hover .v2-giftbar-arrow{transform:translateX(4px)}
.v2-giftbar-short{display:none}
.v2-btn-gift{display:inline-flex;align-items:center;justify-content:center;gap:6px;background:${C.amberWarm};color:${C.parchment};border:none;border-radius:${R.pill};padding:12px 22px;font-family:${F.body};font-size:16px;font-weight:600;cursor:pointer;transition:background-color 150ms ease;box-shadow:${SH.btn};text-decoration:none}
.v2-btn-gift:hover{background:${C.amber}}
.v2-btn-gift-hero{padding:20px 32px;font-size:18px}

/* ── NAV ── */
.v2-nav{position:fixed;top:44px;left:0;right:0;z-index:100;transition:all 300ms ease;padding:24px 32px;border-bottom:1px solid transparent}
.v2-nav.scrolled{background:rgba(251,248,241,0.90);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border-bottom-color:${C.border};padding:16px 32px;box-shadow:${SH.sm}}
.v2-nav-inner{max-width:1152px;margin:0 auto;display:flex;align-items:center;justify-content:space-between}
.v2-nav-brand{display:flex;align-items:center;gap:8px;text-decoration:none;color:${C.ink}}
.v2-nav-logo{width:28px;height:28px;background:${C.ink};border-radius:6px;display:flex;align-items:center;justify-content:center;color:${C.parchment};font-size:13px;font-weight:600;font-family:${F.heading}}
.v2-nav-links{display:flex;align-items:center;gap:32px}
.v2-nav-link{font-size:16px;font-weight:500;color:${ink(0.8)};text-decoration:none;background:none;border:none;cursor:pointer;font-family:${F.body};transition:color 150ms}
.v2-nav-link:hover{color:${C.ink}}
.v2-nav-btns{display:flex;align-items:center;gap:12px}
.v2-nav-mobile{display:none}
.v2-mobile-menu{display:none;position:fixed;inset:0;z-index:200;background:${C.parchment};flex-direction:column}
.v2-mobile-menu.open{display:flex}
.v2-mm-header{display:flex;align-items:center;justify-content:space-between;padding:18px 24px;border-bottom:1px solid ${C.border}}
.v2-mm-links{display:flex;flex-direction:column;padding:10px 0;flex:1 1 auto}
.v2-mm-link{font-size:19px;font-weight:500;color:${C.ink};text-align:left;background:none;border:none;cursor:pointer;font-family:${F.body};padding:16px 24px;text-decoration:none;min-height:44px}
.v2-mm-link:hover{background:${C.tan}}
.v2-mm-actions{display:flex;flex-direction:column;gap:12px;padding:20px 24px calc(24px + env(safe-area-inset-bottom));border-top:1px solid ${C.border}}
.v2-btn-gift-ghost{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:transparent;color:${C.amberWarm};border:1.5px solid ${C.amberWarm}66;border-radius:${R.pill};padding:14px 24px;font-family:${F.body};font-size:16px;font-weight:600;cursor:pointer;text-decoration:none;min-height:48px}
.v2-mm-signin{background:none;border:none;color:${ink(0.7)};font-family:${F.body};font-size:15px;text-decoration:underline;text-underline-offset:3px;cursor:pointer;padding:10px;min-height:44px}

/* ── BUTTONS ── */
.v2-btn-primary{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:${C.ink};color:${C.parchment};border:none;border-radius:${R.pill};padding:12px 24px;font-family:${F.body};font-size:16px;font-weight:500;cursor:pointer;transition:background-color 150ms ease;box-shadow:${SH.btn};text-decoration:none}
.v2-btn-primary:hover{background:#1e2d45}
.v2-btn-primary-hero{padding:20px 32px;font-size:18px}
.v2-btn-primary-hero:hover{transform:scale(1.02)}
.v2-btn-cta{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:${C.amber};color:${C.parchment};border:none;border-radius:${R.pill};padding:12px 24px;font-family:${F.body};font-size:16px;font-weight:500;cursor:pointer;transition:background-color 150ms ease;box-shadow:${SH.btn};text-decoration:none}
.v2-btn-cta:hover{background:${C.amberDark}}
.v2-btn-outline{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:transparent;color:${C.ink};border:1px solid ${ink(0.2)};border-radius:${R.pill};padding:12px 24px;font-family:${F.body};font-size:16px;font-weight:500;cursor:pointer;transition:background-color 150ms ease}
.v2-btn-outline:hover{background:${C.tan}}
.v2-btn-ghost{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:transparent;color:${C.ink};border:none;border-radius:${R.pill};padding:12px 24px;font-family:${F.body};font-size:16px;font-weight:500;cursor:pointer;transition:background-color 150ms ease}
.v2-btn-ghost:hover{background:${C.tan}}
.v2-btn-secondary{display:inline-flex;align-items:center;justify-content:center;gap:8px;background:${C.tan};color:${C.ink};border:1px solid ${ink(0.15)};border-radius:${R.pill};padding:12px 24px;font-family:${F.body};font-size:16px;font-weight:500;cursor:pointer;transition:background-color 150ms ease}
.v2-btn-secondary:hover{background:#e4dece}

/* ── HERO ── */
.v2-hero{padding:236px 24px 128px}
.v2-hero-inner{max-width:1152px;margin:0 auto;display:grid;grid-template-columns:1fr 1fr;gap:80px;align-items:center}
.v2-hero-text{max-width:560px}
.v2-hero-eyebrow{font-family:${F.heading};font-size:0.72rem;letter-spacing:0.18em;text-transform:uppercase;color:${C.amberWarm};font-weight:500;margin-bottom:20px;display:block}
.v2-hero h1{font-size:clamp(2.4rem,5.5vw,3.6rem);font-weight:500;line-height:1.15;margin-bottom:24px;letter-spacing:0.03em}
.v2-hero h1 em{font-style:italic;color:${C.amber}}
.v2-hero-body{font-family:${F.body};font-size:clamp(1.1rem,2vw,1.25rem);color:${C.muted};line-height:1.8;margin-bottom:36px}
.v2-hero-ctas{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:40px}
.v2-trust-row{display:flex;flex-wrap:wrap;gap:24px}
.v2-trust-item{display:flex;align-items:center;gap:6px;font-size:14px;font-weight:500;color:${C.muted}}
.v2-trust-icon{font-size:16px;color:${ink(0.6)}}
.v2-hero-img{position:relative}
.v2-hero-img img{width:100%;border-radius:${R.xl}px;box-shadow:${SH.xl};aspect-ratio:1/1;object-fit:cover;object-position:center 60%}

/* ── SECTIONS ── */
.v2-section{padding:96px 24px}
.v2-section-sand{padding:96px 24px;background:linear-gradient(180deg,#f8f5ef 0%,#f1ebe1 100%);border-top:1px solid rgba(220,216,206,0.4);border-bottom:1px solid rgba(220,216,206,0.4)}
.v2-section-alt{padding:96px 24px;background:rgba(239,235,224,0.5);border-top:1px solid rgba(220,216,206,0.5);border-bottom:1px solid rgba(220,216,206,0.5)}
.v2-section-warm{padding:96px 24px;background:linear-gradient(180deg,#f5f0e8 0%,#ede5d8 100%);border-top:1px solid rgba(210,200,185,0.5);border-bottom:1px solid rgba(210,200,185,0.5)}
.v2-section-dark{padding:128px 24px;background:${C.ink};color:${C.parchment};position:relative;overflow:hidden}
.v2-section-memory{padding:112px 24px;background:linear-gradient(180deg,#f3ede3 0%,#ede6d8 100%);position:relative;overflow:hidden;border-top:1px solid rgba(200,185,165,0.5);border-bottom:1px solid rgba(200,185,165,0.5)}
.v2-section-dark-lead{padding:128px 24px 80px;background:linear-gradient(180deg,${C.ink} 0%,${C.ink} 100%);color:${C.parchment};position:relative;overflow:hidden}
.v2-section-dark-lead::before{content:'';position:absolute;top:0;left:0;right:0;height:80px;background:linear-gradient(180deg,rgba(251,248,241,0.04) 0%,transparent 100%);pointer-events:none}
.v2-section-cta{padding:80px 24px 128px;background:${C.ink};color:${C.parchment};text-align:center;position:relative}
.v2-section-cta::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(201,168,76,0.25),transparent)}
.v2-container{max-width:1152px;margin:0 auto}
.v2-container-narrow{max-width:768px;margin:0 auto}
.v2-container-mid{max-width:1024px;margin:0 auto}
.v2-container-care{max-width:896px;margin:0 auto}
.v2-section-header{text-align:center;margin-bottom:64px}
.v2-section-header h2{font-size:clamp(1.75rem,3.3vw,2.5rem);font-weight:500;margin-bottom:16px;line-height:1.2;letter-spacing:0.02em}
.v2-section-header p{font-family:${F.body};font-size:1.15rem;color:${C.muted};max-width:580px;margin:0 auto;line-height:1.75;font-style:italic}

/* ── AUDIENCE CARDS ── */
.v2-cards-3{display:grid;grid-template-columns:repeat(3,1fr);gap:32px}
.v2-audience-card{background:${C.parchment};border:1px solid rgba(220,216,206,0.60);border-radius:${R.xl}px;padding:32px;cursor:pointer;box-shadow:${SH.sm};transition:box-shadow 200ms ease}
.v2-audience-card:hover{box-shadow:${SH.md}}
.v2-card-icon{width:48px;height:48px;border-radius:50%;background:${ink(0.05)};display:flex;align-items:center;justify-content:center;font-size:22px;margin-bottom:16px}
.v2-audience-card h3{font-size:1.3rem;font-weight:600;margin-bottom:10px;line-height:1.3}
.v2-audience-card p{font-size:1.1rem;color:${C.muted};line-height:1.7}
.v2-card-link{font-size:14px;font-weight:500;color:${ink(0.8)};margin-top:16px;display:inline-block;transition:color 150ms}
.v2-card-link:hover{color:${C.amber}}
.v2-card-btn{display:inline-block;margin-top:20px;padding:11px 24px;border-radius:9999px;font-family:${F.body};font-size:15px;font-weight:700;letter-spacing:0.2px;border:none;cursor:pointer;transition:transform 120ms ease,box-shadow 120ms ease,filter 120ms ease}
.v2-card-btn-write{background:${C.ink};color:${C.parchment}}
.v2-card-btn-gift{background:${C.amberWarm};color:${C.parchment}}
.v2-card-btn-celebrate{background:linear-gradient(180deg,#d9b75f,#c9a84c);color:#1a1206}
.v2-audience-card:hover .v2-card-btn{transform:translateY(-1px);box-shadow:0 6px 18px rgba(15,23,42,0.18);filter:brightness(1.04)}

/* ── INTERIOR WOW ── */
.v2-wow-grid{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center}
.v2-wow-text h2{font-size:48px;font-weight:400;margin-bottom:24px;color:${C.parchment}}
.v2-wow-text h2 em{font-style:italic;color:${C.amberDecor}}
.v2-wow-text p{font-size:18px;color:rgba(255,255,255,0.80);line-height:1.65;margin-bottom:32px}
.v2-wow-list{list-style:none;padding:0;display:flex;flex-direction:column;gap:16px}
.v2-wow-list li{display:flex;align-items:flex-start;gap:12px;font-size:16px;color:rgba(255,255,255,0.90)}
.v2-wow-check{color:${C.amberDecor};font-size:18px;flex-shrink:0;margin-top:2px}
.v2-dot-pattern{position:absolute;inset:0;opacity:0.1;pointer-events:none;background-image:radial-gradient(circle at 50% 50%,#fff 1px,transparent 1px);background-size:40px 40px}
.v2-book-spread{background:#fdfbf7;color:#1a1a1a;padding:32px 48px;border-radius:8px;box-shadow:${SH.xxl};display:flex;gap:32px;transform:rotate(2deg);transition:transform 700ms ease;max-width:640px}
.v2-book-spread:hover{transform:rotate(0deg)}
.v2-spread-left{flex:1;border-right:1px solid #e0dcd3;padding-right:32px;position:relative;display:flex;flex-direction:column;justify-content:space-between}
.v2-spread-left::after{content:'';position:absolute;top:0;right:0;height:100%;width:1px;background:linear-gradient(180deg,transparent,#c8c4b9,transparent)}
.v2-spread-right{flex:1;display:flex;flex-direction:column;justify-content:space-between}
.v2-spread-chapter{font-size:10px;letter-spacing:3px;text-transform:uppercase;color:#888;margin-bottom:24px;text-align:center}
.v2-spread-title{font-family:${F.heading};font-size:24px;margin-bottom:20px}
.v2-spread-body{font-size:14px;line-height:1.85;color:#444;text-indent:16px;margin-bottom:12px}
.v2-spread-body:first-of-type{text-indent:0}
.v2-spread-pagenum{font-size:11px;color:#aaa;text-align:center;margin-top:auto;padding-top:24px}
.v2-spread-photo{background:#eae6db;aspect-ratio:4/5;border-radius:2px;overflow:hidden;margin-bottom:16px;position:relative}
.v2-spread-photo img{width:100%;height:100%;object-fit:cover;filter:grayscale(1);opacity:0.8}
.v2-spread-qr{position:absolute;bottom:8px;right:8px;background:rgba(255,255,255,0.9);padding:4px;border-radius:2px}
.v2-spread-qr span{display:block;width:20px;height:20px;border:1px solid ${ink(0.2)};font-size:7px;display:flex;align-items:center;justify-content:center;font-family:monospace}
.v2-spread-caption{font-size:12px;font-style:italic;color:#666;text-align:center;margin-bottom:16px}

/* ── HOW IT WORKS ── */
.v2-steps-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:48px;position:relative}
.v2-steps-line{display:none;position:absolute;top:48px;left:16%;right:16%;height:1px;background:${C.border}}
.v2-step{display:flex;flex-direction:column;align-items:center;text-align:center;position:relative;z-index:1}
.v2-step-circle{width:96px;height:96px;border-radius:50%;display:flex;align-items:center;justify-content:center;margin-bottom:24px;box-shadow:${SH.sm}}
.v2-step-circle-outline{background:${C.parchment};border:1px solid ${C.border}}
.v2-step-circle-filled{background:${C.amberWarm};border:none}
.v2-step-num{font-family:${F.heading};font-size:30px}
.v2-step h3{font-size:1.2rem;font-weight:500;margin-bottom:12px}
.v2-step p{font-size:1.1rem;color:${C.muted};line-height:1.7}

/* ── BOOK PRODUCT ── */
.v2-product-grid{display:grid;grid-template-columns:1fr 1fr;gap:64px;align-items:center}
.v2-product-img{border-radius:${R.lg}px;box-shadow:${SH.lg};width:100%}
.v2-product-specs{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:32px}
.v2-spec h4{font-family:${F.body};font-size:1.05rem;font-weight:500;margin-bottom:8px;display:flex;align-items:center;gap:8px}
.v2-spec-dot{width:6px;height:6px;border-radius:50%;background:${C.amberDecor};flex-shrink:0}
.v2-spec p{font-size:1rem;color:${C.muted};line-height:1.6}

/* ── FEATURES GRID ── */
.v2-features-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px}
.v2-feature{padding:24px}
.v2-feature-icon{font-size:28px;margin-bottom:16px;display:block}
.v2-feature h3{font-size:1.15rem;font-weight:500;margin-bottom:8px}
.v2-feature p{font-size:1.05rem;color:${C.muted};line-height:1.7}

/* ── MEMORY CARE ── */
.v2-care-glow{position:absolute;top:0;right:0;width:256px;height:256px;background:rgba(124,45,18,0.05);border-radius:50%;filter:blur(48px);transform:translate(50%,-50%);pointer-events:none}
.v2-care-heart{font-size:40px;color:rgba(251,248,241,0.7);margin-bottom:24px}
.v2-care-body{font-size:16px;color:${C.muted};line-height:1.85;margin-bottom:16px;text-align:left;max-width:640px;margin-left:auto;margin-right:auto}
.v2-care-features{list-style:none;padding:0;margin:32px auto;max-width:640px;display:flex;flex-direction:column;gap:12px}
.v2-care-features li{display:flex;align-items:flex-start;gap:14px;font-size:16px;color:${C.muted};line-height:1.6;text-align:left}
.v2-care-feature-icon{font-size:18px;flex-shrink:0;margin-top:2px}
.v2-care-note{font-size:14px;color:${C.muted};margin-top:16px}

/* ── QR / VOICE & VIDEO ── */
.v2-section-qr{padding:96px 24px 80px;background:${C.ink};position:relative}
.v2-section-qr::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(251,248,241,0.15),transparent)}
.v2-section-qr h2{color:${C.parchment}}
.v2-section-qr p{color:rgba(251,248,241,0.8)}
.v2-qr-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px;max-width:900px;margin:48px auto}
.v2-qr-card{background:rgba(251,248,241,0.04);border:1px solid rgba(251,248,241,0.1);border-radius:${R.lg}px;padding:32px 24px;text-align:center;transition:transform 300ms,border-color 300ms}
.v2-qr-card:hover{transform:translateY(-4px);border-color:rgba(251,248,241,0.25)}
.v2-qr-icon{font-size:28px;margin-bottom:16px;display:block}
.v2-qr-card h4{font-family:${F.heading};font-size:1.15rem;font-weight:500;color:${C.parchment};margin-bottom:10px}
.v2-qr-card p{font-size:1.05rem;color:rgba(251,248,241,0.6);line-height:1.7}
.v2-qr-hook{font-family:${F.heading};font-size:1.2rem;font-style:italic;color:rgba(251,248,241,0.7);text-align:center;max-width:560px;margin:48px auto 0;line-height:1.6}

/* ── AI SECTION ── */
.v2-ai-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:32px}
.v2-ai-card{padding:32px 24px;background:${C.white};border:1px solid rgba(220,216,206,0.6);border-radius:${R.xl}px;transition:box-shadow 200ms,transform 200ms}
.v2-ai-card:hover{box-shadow:${SH.md};transform:translateY(-4px)}
.v2-ai-icon{font-size:24px;margin-bottom:14px;display:block}
.v2-ai-card h3{font-size:1.15rem;font-weight:500;letter-spacing:0.02em;margin-bottom:10px}
.v2-ai-card p{font-size:1.05rem;color:${C.muted};line-height:1.7}

/* ── INTERIOR BOOK PREVIEW SECTION ── */
.v2-section-book-preview{padding:100px 32px;background:linear-gradient(180deg,#f5f0e8 0%,#ede5d8 100%);border-top:1px solid rgba(210,200,185,0.5);border-bottom:1px solid rgba(210,200,185,0.5)}
.v2-section-eyebrow{font-family:${F.heading};font-size:0.7rem;letter-spacing:0.12em;text-transform:uppercase;color:${C.muted};font-weight:500;margin-bottom:12px;display:block}
.v2-3d-book{transform:rotateY(-8deg) rotateX(2deg)}

/* ── PRICING ── */
.v2-pricing-card{background:${C.parchment};border:1px solid ${C.border};border-radius:24px;padding:48px;box-shadow:${SH.sm};position:relative;max-width:768px;margin:0 auto}
.v2-pricing-badge{position:absolute;top:0;left:50%;transform:translate(-50%,-50%);background:${C.amber};color:${C.parchment};padding:4px 16px;border-radius:${R.pill};font-size:14px;font-weight:500;letter-spacing:0.05em}
.v2-pricing-grid{display:grid;grid-template-columns:1fr 1fr;gap:48px;text-align:left}
.v2-pricing-tier h3{font-family:${F.heading};font-size:24px;margin-bottom:8px}
.v2-pricing-amount{font-size:36px;font-weight:600;margin-bottom:16px}
.v2-pricing-amount span{font-size:16px;color:${C.muted};font-weight:400}
.v2-pricing-list{list-style:none;padding:0;display:flex;flex-direction:column;gap:12px}
.v2-pricing-list li{display:flex;align-items:center;gap:8px;font-size:1.05rem;color:${C.muted}}
.v2-pricing-check{color:${ink(0.6)};font-size:16px;flex-shrink:0}
.v2-pricing-divider{width:1px;background:${C.border};margin:0 auto}

/* ── TRUST BAR ── */
.v2-trust-bar{padding:48px 24px;border-top:1px solid rgba(220,216,206,0.5)}
.v2-trust-bar-inner{display:flex;justify-content:center;gap:48px;flex-wrap:wrap;max-width:900px;margin:0 auto}
.v2-trust-bar-item{display:flex;align-items:center;gap:8px;font-size:14px;font-weight:500;color:${C.muted}}

/* ── FOOTER ── */
.v2-footer{padding:48px 24px;border-top:1px solid rgba(220,216,206,0.5);background:${C.parchment}}
.v2-footer-inner{max-width:1152px;margin:0 auto;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:24px}
.v2-footer-brand{display:flex;align-items:center;gap:8px}
.v2-footer-links{display:flex;gap:24px}
.v2-footer-link{font-size:14px;color:${C.muted};text-decoration:none;background:none;border:none;cursor:pointer;font-family:${F.body};transition:color 150ms}
.v2-footer-link:hover{color:${C.ink}}
.v2-footer-social{display:flex;gap:16px;align-items:center}
.v2-footer-social a{display:inline-flex;color:${C.muted};transition:color 150ms}
.v2-footer-social a:hover{color:${C.ink}}
.v2-footer-social svg{width:20px;height:20px;display:block}
.v2-footer-legal{font-size:14px;color:${C.muted}}

/* ── RESPONSIVE ── */
@media(min-width:769px){.v2-steps-line{display:block}}
@media(max-width:1199px){
  .v2-nav-links{display:none}
  .v2-nav-btns{display:none}
  .v2-nav-mobile{display:flex;align-items:center;gap:8px}
  .v2-nav-brand span:last-child{display:none}
  /* F-13b: more vertical rhythm between the fixed $30 banner (44px) and the nav on mobile */
  .v2-nav{padding:36px 20px 14px}
  .v2-nav.scrolled{padding:14px 20px}
}
@media(max-width:1024px){
  .v2-hero-inner{grid-template-columns:1fr;gap:48px;text-align:center}
  .v2-hero-text{max-width:640px;margin:0 auto}
  .v2-hero h1{font-size:60px}
  .v2-hero-ctas{justify-content:center}
  .v2-trust-row{justify-content:center}
  .v2-wow-grid{grid-template-columns:1fr;gap:48px}
  .v2-product-grid{grid-template-columns:1fr;gap:32px}
}
@media(max-width:768px){
  .v2-nav-links{display:none}
  .v2-nav-btns{display:none}
  .v2-nav-mobile{display:flex;align-items:center;gap:8px}
  .v2-giftbar{font-size:13.5px;gap:7px}
  .v2-giftbar-full{display:none}
  .v2-giftbar-short{display:inline}
  .v2-hero{padding:172px 24px 80px}
  .v2-hero h1{font-size:2.4rem}
  .v2-hero-body{font-size:18px}
  .v2-section{padding:64px 24px}
  .v2-section-sand{padding:64px 24px}
  .v2-section-alt{padding:64px 24px}
  .v2-section-warm{padding:64px 24px}
  .v2-section-dark{padding:80px 24px}
  .v2-section-memory{padding:64px 24px}
  .v2-section-memory .v2-care-grid-inner{grid-template-columns:1fr !important}
  .v2-section-dark-lead{padding:80px 24px 56px}
  .v2-section-cta{padding:56px 24px 80px}
  .v2-section-qr{padding:64px 24px 56px}
  .v2-cards-3{grid-template-columns:1fr}
  .v2-steps-grid{grid-template-columns:1fr;gap:32px}
  .v2-ai-grid{grid-template-columns:1fr}
  .v2-features-grid{grid-template-columns:1fr}
  .v2-qr-grid{grid-template-columns:1fr;max-width:400px}
  .v2-pricing-grid{grid-template-columns:1fr;gap:32px}
  .v2-trust-bar-inner{flex-direction:column;align-items:center;gap:12px;max-width:320px}
  .v2-book-spread{flex-direction:column;padding:24px;transform:none}
  .v2-spread-left{border-right:none;padding-right:0;border-bottom:1px solid #e0dcd3;padding-bottom:24px}
  .v2-footer-inner{flex-direction:column;text-align:center;gap:16px}
  .v2-footer-social{justify-content:center}
  .v2-section-header h2{font-size:1.5rem}
  .v2-interior-right{margin-top:28px;border-top:1px solid #e8e0d4;border-radius:4px !important}
  .open-book{display:block !important;max-width:100% !important;box-shadow:0 12px 36px rgba(42,37,32,0.14) !important}
  .v2-spine-divider{display:none}
  .v2-section-book-preview{padding:64px 20px}
  .v2-nav-brand span:last-child{display:none}
  .v2-pricing-card{padding:32px 20px}
  .v2-pricing-amount{font-size:28px}
  .v2-section-eyebrow{font-size:13px}
  .v2-3d-book{transform:rotateY(-4deg) rotateX(1deg)}
}
`;

export default function LandingPageV2({ onGetStarted, onSignIn }) {
  const [navScrolled, setNavScrolled] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const sale = useSaleOffer(); // F-163: cap-aware sale price for the pricing section (honest revert if capped)

  React.useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (sel) => document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="lpv2">
      <style>{CSS}</style>

      {/* ══════════ SALE BAR (TALE30 — $30 off) ══════════ */}
      {/* F-163-FINAL: driven by the same cap-aware hook as every other surface — hides cleanly when the
          founder switches the sale off (active:false). No hardcoded discount that outlives the sale. */}
      {sale.saleActive && (
      <button className="v2-giftbar" onClick={() => { trackEvent('promo_banner_click', { code: 'TALE30', location: 'banner' }); onGetStarted('own_story'); }}>
        <Sparkle size={16} weight="fill" />
        <span className="v2-giftbar-full">Get $30 off your hardcover book with code TALE30</span>
        <span className="v2-giftbar-short">$30 off with code TALE30</span>
        <span className="v2-giftbar-arrow">→</span>
      </button>
      )}

      {/* ══════════ NAV ══════════ */}
      {/* F-163-FINAL: when the sale bar is hidden (sale off), collapse the nav's 44px bar-clearance so the
          "off" state has no empty strip. */}
      <nav className={`v2-nav${navScrolled ? ' scrolled' : ''}`} style={!sale.saleActive ? { top: 0 } : undefined}>
        <div className="v2-nav-inner">
          <div className="v2-nav-brand">
            <span style={{ fontFamily: F.heading, fontSize: '1.4rem', fontWeight: 700, letterSpacing: 1 }}>Talebound</span>
            <span style={{ fontFamily: F.body, fontSize: '0.65rem', color: ink(0.5), fontStyle: 'italic', letterSpacing: 1, marginLeft: 4 }}>Stories bound to last</span>
          </div>
          <div className="v2-nav-links">
            <button className="v2-nav-link" onClick={() => scrollTo('.v2-steps-section')}>How It Works</button>
            <a className="v2-nav-link" href="/celebration">Celebration Books</a>
            <button className="v2-nav-link" onClick={() => scrollTo('.v2-pricing-section')}>Pricing</button>
          </div>
          <div className="v2-nav-btns">
            <a className="v2-btn-gift" href="/gift" onClick={() => trackEvent('gift_cta_click', { location: 'nav', source: 'landing' })}><Gift size={16} weight="fill" /> Gift</a>
            <button className="v2-btn-ghost" onClick={onSignIn}>Sign In</button>
            <button className="v2-btn-primary" onClick={() => onGetStarted('own_story')}>Get Started Free</button>
          </div>
          <div className="v2-nav-mobile">
            <button style={{ background: 'none', border: `1px solid ${ink(0.2)}`, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: C.ink, display: 'flex', alignItems: 'center' }} onClick={() => setMobileMenuOpen(p => !p)}>{mobileMenuOpen ? <X size={20} /> : <MenuIcon size={20} />}</button>
            <button className="v2-btn-primary" style={{ fontSize: 13, padding: '8px 16px', whiteSpace: 'nowrap' }} onClick={() => onGetStarted('own_story')}>Get Started</button>
          </div>
        </div>
      </nav>
      {/* F-13b: full-sheet OPAQUE mobile menu — wordmark + X header only, links (no Memory Care),
          one-voice actions at the bottom. */}
      <div className={`v2-mobile-menu${mobileMenuOpen ? ' open' : ''}`}>
        <div className="v2-mm-header">
          <span style={{ fontFamily: F.heading, fontSize: '1.4rem', fontWeight: 700, letterSpacing: 1, color: C.ink }}>Talebound</span>
          <button aria-label="Close menu" onClick={() => setMobileMenuOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.ink, padding: 8, display: 'flex' }}><X size={26} /></button>
        </div>
        <div className="v2-mm-links">
          <button className="v2-mm-link" onClick={() => { setMobileMenuOpen(false); scrollTo('.v2-steps-section'); }}>How It Works</button>
          <a className="v2-mm-link" href="/celebration">Celebration Books</a>
          <button className="v2-mm-link" onClick={() => { setMobileMenuOpen(false); scrollTo('.v2-pricing-section'); }}>Pricing</button>
        </div>
        <div className="v2-mm-actions">
          <button className="v2-btn-primary" style={{ width: '100%' }} onClick={() => { setMobileMenuOpen(false); onGetStarted('own_story'); }}>Get Started Free</button>
          <a className="v2-btn-gift-ghost" style={{ width: '100%' }} href="/gift" onClick={() => trackEvent('gift_cta_click', { location: 'nav_mobile', source: 'landing' })}><Gift size={16} weight="fill" /> Gift Someone Talebound</a>
          <button className="v2-mm-signin" onClick={() => { setMobileMenuOpen(false); onSignIn(); }}>Sign in</button>
        </div>
      </div>

      {/* ══════════ HERO ══════════ */}
      <section className="v2-hero">
        <div className="v2-hero-inner">
          <div className="v2-hero-text">
            <span className="v2-hero-eyebrow">Your stories. Your book.</span>
            <h1>Turn your life stories into <em>a real hardcover book</em></h1>
            <p className="v2-hero-body">You just talk. Talebound guides you story by story and turns your words into beautifully written chapters, even if you don't know where to start.</p>
            <div className="v2-hero-ctas">
              <button className="v2-btn-primary v2-btn-primary-hero" onClick={() => onGetStarted('own_story')}>Start Your Book For Free</button>
              <a className="v2-btn-gift v2-btn-gift-hero" href="/gift" onClick={() => trackEvent('gift_cta_click', { location: 'hero', source: 'landing' })}><Gift size={20} weight="fill" /> Gift Someone Talebound</a>
            </div>
            <div className="v2-trust-row">
              <div className="v2-trust-item"><ShieldCheck weight="duotone" size={16} style={{ color: ink(0.6) }} /> AES-256 encrypted</div>
              <div className="v2-trust-item"><MapPin weight="duotone" size={16} style={{ color: ink(0.6) }} /> Printed in the USA</div>
            </div>
          </div>
          <div className="v2-hero-img">
            <img src="/talebound-books-trio.jpg" alt="Three real Talebound hardcover books in charcoal, burgundy, and floral cover styles on a table with coffee" />
          </div>
        </div>
      </section>

      {/* ══════════ HOW IT WORKS (moved up: it's the clearest "what is this" answer) ══════════ */}
      <section className="v2-section v2-steps-section">
        <div className="v2-container-mid">
          <div className="v2-section-header">
            <h2>Three steps to a real hardcover book</h2>
            <p>No writing experience needed. Your first story takes five minutes.</p>
          </div>
          <div className="v2-steps-grid">
            <div className="v2-steps-line" />
            <div className="v2-step">
              <div className="v2-step-circle v2-step-circle-outline"><span className="v2-step-num" style={{ color: C.ink }}>1</span></div>
              <h3>Tell Your Stories</h3>
              <p>Answer a question, record an interview, or write freely. Talebound guides you gently. No blank page, no pressure.</p>
            </div>
            <div className="v2-step">
              <div className="v2-step-circle v2-step-circle-outline"><span className="v2-step-num" style={{ color: C.ink }}>2</span></div>
              <h3>Build Your Book</h3>
              <p>Each story becomes a chapter. Add photos, write a dedication, preview every page exactly as it prints.</p>
            </div>
            <div className="v2-step">
              <div className="v2-step-circle v2-step-circle-filled"><span className="v2-step-num" style={{ color: C.parchment }}>3</span></div>
              <h3>We Print &amp; Ship</h3>
              <p>A beautiful hardcover arrives at your door. Your name on the cover. Your words inside. A gift that lasts generations.</p>
            </div>
          </div>
          <div style={{ marginTop: 44 }}>
            <p style={{ textAlign: 'center', fontStyle: 'italic', color: C.muted, marginBottom: 16, fontSize: '1rem' }}>See exactly how it works:</p>
            <VideoEmbed videoId="YBzPWm1EcjU" title="How Talebound works" maxWidth={720} />
          </div>
        </div>
      </section>

      {/* ══════════ WHY TALEBOUND (comparison) ══════════ */}
      <section className="v2-section-sand">
        <div className="v2-container-mid">
          <div className="v2-section-header">
            <p className="v2-section-eyebrow" style={{ color: C.amberWarm }}>Why Talebound</p>
            <h2>Most life-story apps feel like homework. Talebound feels like a conversation.</h2>
            <p>It listens, asks what comes next, and turns the conversation into beautifully written chapters. Free to start.</p>
          </div>
          <div style={{ maxWidth: 820, margin: '0 auto', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', boxShadow: SH.sm }}>
            <div style={{ display: 'flex', background: C.parchment }}>
              <div style={{ flex: 1, padding: '14px 24px', fontFamily: F.heading, fontWeight: 500, color: ink(0.4), fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>The old way</div>
              <div style={{ flex: 1, padding: '14px 24px', fontFamily: F.heading, fontWeight: 600, color: C.amber, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>The Talebound way</div>
            </div>
            {[
              { old: "A flat question, then you're on your own", neu: "A live interview that listens and picks up on your details", lead: true },
              { old: "You write every word yourself", neu: "It writes your story beautifully, from your conversation", lead: true },
              { old: "Pay up front, before you've tried it", neu: "Free to start. Pay only when you love it.", lead: true },
              { old: "One emailed question a week, for a year (ouch)", neu: "Talk whenever you want, and watch it come together" },
              { old: "A chore most people abandon", neu: "Fun enough that you actually finish" },
            ].map((row, i) => (
              <div key={i} style={{ display: 'flex', borderTop: `1px solid ${C.border}` }}>
                <div style={{ flex: 1, padding: '20px 24px', color: ink(0.45), fontSize: 16, lineHeight: 1.6, display: 'flex', gap: 10, background: i % 2 === 0 ? C.parchment : 'rgba(251,248,241,0.5)' }}>
                  <span style={{ color: ink(0.25), flexShrink: 0, marginTop: 2 }}>✕</span>
                  <span style={{ fontWeight: row.lead ? 500 : 400 }}>{row.old}</span>
                </div>
                <div style={{ flex: 1, padding: '20px 24px', background: row.lead ? 'rgba(124,45,18,0.07)' : 'rgba(124,45,18,0.03)', color: C.ink, fontSize: 16, lineHeight: 1.6, display: 'flex', gap: 10 }}>
                  <span style={{ color: C.amber, flexShrink: 0, marginTop: 2 }}>✓</span>
                  <span style={{ fontWeight: row.lead ? 600 : 500 }}>{row.neu}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ AUDIENCES ══════════ */}
      <section className="v2-section-alt">
        <div className="v2-container">
          <div className="v2-section-header">
            <h2>Three reasons people find us</h2>
            <p>Whether it's your story, someone you love, or a gift for someone special, Talebound makes it a real book.</p>
          </div>
          <div className="v2-cards-3">
            <div className="v2-audience-card" onClick={() => onGetStarted('own_story')}>
              <div className="v2-card-icon"><Feather size={24} weight="duotone" /></div>
              <h3>I want to write my own story</h3>
              <p>You have memories worth keeping. Talebound asks the right questions, helps you find the words, and turns your answers into chapters of a real printed book.</p>
              <span className="v2-card-btn v2-card-btn-write">Start writing →</span>
            </div>
            <div className="v2-audience-card" onClick={() => { trackEvent('gift_cta_click', { location: 'card', source: 'landing' }); window.location.href = '/gift'; }}>
              <div className="v2-card-icon"><Gift size={24} weight="duotone" /></div>
              <h3>I want to gift Talebound to someone</h3>
              <p>Give a parent or loved one the gift of writing their own life story. You buy it, they write it, and the finished book is theirs to keep.</p>
              <span className="v2-card-btn v2-card-btn-gift">Gift someone <span style={{ whiteSpace: 'nowrap' }}>Talebound →</span></span>
            </div>
            <div className="v2-audience-card" onClick={() => { trackEvent('celebration_cta_click', { location: 'card' }); window.location.href = '/celebration'; }}>
              <div className="v2-card-icon"><Confetti size={24} weight="duotone" /></div>
              <h3>I want to make a book about someone</h3>
              <p>Gather stories from your whole family into a beautiful book for Mom, Dad, or anyone you love. You organize, everyone contributes.</p>
              <span className="v2-card-btn v2-card-btn-celebrate">Start a Celebration <span style={{ whiteSpace: 'nowrap' }}>Book →</span></span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ INTERIOR PAGE PREVIEW ══════════ */}
      <section className="v2-section-book-preview">
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div className="v2-section-header">
            <p className="v2-section-eyebrow">See inside</p>
            <h2>Every page, exactly as it prints</h2>
            <p>Your stories set in elegant typography with photos. A book that feels handmade, not generated.</p>
          </div>
          <div className="open-book" style={{ display: 'flex', maxWidth: 820, margin: '0 auto', borderRadius: 4, boxShadow: '0 20px 60px rgba(42,37,32,0.18), 0 4px 16px rgba(42,37,32,0.08)', position: 'relative' }}>
            {/* Spine divider */}
            <div className="v2-spine-divider" style={{ position: 'absolute', top: 8, bottom: 8, left: '50%', width: 2, transform: 'translateX(-50%)', background: 'linear-gradient(180deg,transparent,rgba(0,0,0,0.06) 15%,rgba(0,0,0,0.1) 50%,rgba(0,0,0,0.06) 85%,transparent)', zIndex: 10 }} />
            {/* LEFT PAGE — story text */}
            <div style={{ flex: 1, background: '#faf8f3', padding: '44px 40px 36px', position: 'relative', minHeight: 420, overflow: 'hidden', borderRadius: '4px 0 0 4px', boxShadow: 'inset -4px 0 12px rgba(0,0,0,0.03)' }}>
              <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', letterSpacing: 3, color: '#b0a090', textTransform: 'uppercase', marginBottom: 12 }}>Chapter Five</div>
              <div style={{ fontFamily: "'Lora', serif", fontSize: '1.5rem', fontWeight: 600, color: '#3a2210', lineHeight: 1.3, marginBottom: 6 }}>The Quiet<br />Between Waves</div>
              <div style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '1.05rem', color: '#b0a090', marginBottom: 20 }}>As told by Sarah Mitchell</div>
              <div style={{ width: 40, height: 1, background: '#d4c4a8', marginBottom: 20 }} />
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#5c4a38', lineHeight: 1.85 }}><span style={{ fontFamily: "'Lora', serif", fontSize: '3rem', fontWeight: 700, float: 'left', lineHeight: 0.8, margin: '4px 10px 0 0', color: '#3a2210' }}>W</span>e almost didn't go. The baby had a cold and Jake had just started saying "Mama" every time I left the room. Ten years of marriage and this was the first time we'd gone anywhere without them. Just two nights. My mother practically pushed us out the door.</p>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#5c4a38', lineHeight: 1.85, textIndent: 16 }}>The first hour at the beach, neither of us knew what to do with ourselves. No sippy cups to fill. No one climbing into our laps. David checked his phone three times before I took it out of his hand and put it in my bag.</p>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#5c4a38', lineHeight: 1.85, textIndent: 16 }}>We walked down to the water right before sunset. Barefoot, like we used to before any of it. The house, the minivan, the plastic toys in every room. He took my hand and for a minute it felt like we were twenty-two again.</p>
              <div style={{ position: 'absolute', bottom: 20, left: 40, right: 40, display: 'flex', justifyContent: 'space-between', fontFamily: "'Lora', serif", fontSize: '0.55rem', color: '#b0a090', letterSpacing: 1 }}>
                <span>The Mitchell Family</span>
                <span>34</span>
              </div>
            </div>
            {/* RIGHT PAGE — photo + continued text */}
            <div className="v2-interior-right" style={{ flex: 1, background: '#faf8f3', padding: '44px 40px 36px', position: 'relative', minHeight: 420, overflow: 'hidden', borderRadius: '0 4px 4px 0', boxShadow: 'inset 4px 0 12px rgba(0,0,0,0.03)' }}>
              <div style={{ width: '100%', marginBottom: 20, position: 'relative' }}>
                <div style={{ width: '100%', aspectRatio: '4/3', background: '#e8e0d4', borderRadius: 2, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src="/landing-interior-photo.jpg" alt="Couple walking on the beach" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
                <div style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '0.92rem', color: '#b0a090', textAlign: 'center', marginTop: 8, lineHeight: 1.5 }}>David and Sarah, first trip alone in ten years.</div>
              </div>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#5c4a38', lineHeight: 1.85 }}>Then he said, "Do you think Jake's eating his vegetables?" And I laughed so hard I had to stop walking. Because I'd been thinking the exact same thing. Not about us. Not about the ocean. About whether my mother remembered that Emma won't sleep without the hallway light on.</p>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#5c4a38', lineHeight: 1.85 }}>We stood there watching the waves and talked about our kids for an hour straight. Every story led back to them. That's when it hit me. We didn't need to get away from our life. We just needed a quiet place to realize how much we loved it.</p>
              <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#5c4a38', lineHeight: 1.85 }}>We called home before the sun was all the way down. Jake said "Mama" and I cried right there on the beach. David pretended he wasn't crying too.</p>
              <div style={{ position: 'absolute', bottom: 20, left: 40, right: 40, display: 'flex', justifyContent: 'space-between', fontFamily: "'Lora', serif", fontSize: '0.55rem', color: '#b0a090', letterSpacing: 1 }}>
                <span>35</span>
                <span>Our Family Story</span>
              </div>
            </div>
          </div>
          <p style={{ textAlign: 'center', marginTop: 28, fontFamily: F.heading, fontSize: '1.05rem', fontStyle: 'italic', color: C.muted }}>Every chapter is laid out in print-ready typography with your photos alongside</p>
        </div>
      </section>

      {/* ══════════ BOOK SHOWCASE ══════════ */}
      <section className="v2-section">
        <div className="v2-container">
          <div className="v2-product-grid">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              {/* Real customer hardcover (floral cover) */}
              <img src="/talebound-book-photo.jpg" alt="A real Talebound hardcover book with a floral cover and a family photo, resting on a marble table"
                style={{ width: '100%', maxWidth: 460, borderRadius: 12, boxShadow: '0 22px 55px rgba(42,37,32,0.22), 0 6px 18px rgba(42,37,32,0.10)', display: 'block' }} />
            </div>
            <div>
              <h2 style={{ fontFamily: F.heading, fontSize: 36, fontWeight: 400, marginBottom: 16 }}>A real hardcover book. Made to <em style={{ fontStyle: 'italic' }}>last.</em></h2>
              <p style={{ fontSize: 18, color: C.muted, lineHeight: 1.65, marginBottom: 8 }}>Premium printed, coated paper, elegant typography. This isn't a printout. It's the kind of book that sits on the coffee table for decades.</p>
              <div className="v2-product-specs">
                <div className="v2-spec"><h4><span className="v2-spec-dot" />8.5" × 11" Hardcover</h4><p>Hardcover quality you'll be proud to display</p></div>
                <div className="v2-spec"><h4><span className="v2-spec-dot" />Full Color Photos</h4><p>Your photos alongside your stories, professionally laid out</p></div>
                <div className="v2-spec"><h4><span className="v2-spec-dot" />Three Cover Styles</h4><p>Choose the look that tells your story</p></div>
                <div className="v2-spec"><h4><span className="v2-spec-dot" />Preview Before Ordering</h4><p>See every page exactly as it prints</p></div>
                <div className="v2-spec"><h4><span className="v2-spec-dot" />Additional Copies</h4><p>Order more anytime at a discounted rate</p></div>
              </div>
              <button className="v2-btn-primary" style={{ marginTop: 32 }} onClick={() => onGetStarted('own_story')}>Start Building For Free</button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ MEMORY CARE ══════════ */}
      <section className="v2-section-memory v2-memory-section">
        {/* Soft decorative glow */}
        <div style={{ position: 'absolute', top: -80, right: -40, width: 300, height: 300, background: 'radial-gradient(circle, rgba(124,45,18,0.06), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -60, left: -40, width: 240, height: 240, background: 'radial-gradient(circle, rgba(124,45,18,0.04), transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

        <div className="v2-container-care" style={{ position: 'relative', zIndex: 1 }}>
          {/* Label badge — strong, top-center */}
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, padding: '10px 24px', background: C.ink, borderRadius: R.pill }}>
              <Heart size={16} weight="duotone" style={{ color: C.parchment }} />
              <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.parchment }}>Memory Care</span>
            </div>
          </div>

          {/* F-13: Memory Care is GATED (coming soon). Honest treatment consistent with the in-app
              placeholder — no "always free ever" promise about a feature that is not live, and no
              gift-purchase language (Memory Care is a separate, future product from the paid Gift card). */}
          <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto' }}>
            <h2 style={{ fontFamily: F.heading, fontSize: 42, fontWeight: 400, marginBottom: 20, color: C.ink }}>Memory Care is coming soon.</h2>
            <p style={{ fontSize: 18, color: C.muted, lineHeight: 1.65, marginBottom: 32 }}>Capturing a loved one's stories deserves extra care. We're getting it right first.</p>
            {/* F-13: the /feedback endpoint rejects unauthenticated POSTs (verified 401), so a landing
                email field would silently fail for logged-out visitors — per the founder's hard
                condition, this is a plain notice, not a capture. Logged-in interest is captured by the
                in-app Memory Care sensor (F-12). */}
            <p style={{ fontSize: 15, color: C.muted, fontStyle: 'italic' }}>We'll announce it here when it's ready.</p>
          </div>
        </div>
      </section>

      {/* ══════════ AI FEATURES ══════════ */}
      <section className="v2-section-alt">
        <div className="v2-container" style={{ maxWidth: 960 }}>
          <div className="v2-section-header">
            <h2>You talk, we handle the rest</h2>
            <p>You don't need to be a writer. Just start sharing. We'll take care of the rest.</p>
          </div>
          <div className="v2-ai-grid">
            <div className="v2-ai-card">
              <BookOpenText weight="duotone" size={24} style={{ marginBottom: 14 }} />
              <h3>Guided Questions</h3>
              <p>Answer one question and we'll guide you deeper, surfacing memories you forgot you had.</p>
            </div>
            <div className="v2-ai-card">
              <Heart size={24} weight="duotone" style={{ marginBottom: 14 }} />
              <h3>Interview Mode</h3>
              <p>Sit with a loved one, including someone with dementia or Alzheimer's, while the app guides a gentle session. It remembers what they've shared before.</p>
            </div>
            <div className="v2-ai-card">
              <Feather size={24} weight="duotone" style={{ marginBottom: 14 }} />
              <h3>Write Freely</h3>
              <p>Write on your own. We'll clean up grammar and flow while keeping your voice exactly as it is.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ AI PHOTO ENHANCEMENT ══════════ */}
      <section className="v2-section">
        <div className="v2-container">
          <div className="v2-section-header">
            <p className="v2-section-eyebrow" style={{ color: C.amber }}>Photo Enhancement</p>
            <h2>Professional-grade enhancement for every photo</h2>
            <p>The same enhancement technology used by professional photographers. Now built into your book. Every photo optimized for how ink renders on paper.</p>
          </div>
          <div className="v2-features-grid" style={{ maxWidth: 800, margin: '0 auto' }}>
            <div className="v2-feature"><Sparkle weight="duotone" size={28} style={{ marginBottom: 16, color: C.ink }} /><h3>Print-Ready Upscaling</h3><p>Brings every photo to 300 DPI at full page size</p></div>
            <div className="v2-feature"><Sparkle weight="duotone" size={28} style={{ marginBottom: 16, color: C.ink }} /><h3>Face Clarity</h3><p>Sharpens faces while keeping them natural</p></div>
            <div className="v2-feature"><Sparkle weight="duotone" size={28} style={{ marginBottom: 16, color: C.ink }} /><h3>Detail Recovery</h3><p>Restores fine detail lost in phone compression</p></div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 24px', background: C.tan, border: `1px solid ${C.border}`, borderRadius: R.pill, fontSize: 14, fontWeight: 500, color: C.ink }}>10 photo enhancements included</span>
            <p style={{ fontSize: 14, color: C.muted, marginTop: 12 }}>Studio-grade enhancement, included free during beta</p>
          </div>
        </div>
      </section>

      {/* ══════════ PRICING ══════════ */}
      <section className="v2-section-sand v2-pricing-section">
        <div className="v2-container-narrow">
          <div className="v2-section-header">
            <h2>Simple, honest pricing.</h2>
            <p>No subscriptions. No hidden fees. Write for free, pay only when you print.</p>
          </div>
          <div className="v2-pricing-card">
            <div className="v2-pricing-badge">MOST POPULAR</div>
            <div className="v2-pricing-grid">
              <div className="v2-pricing-tier">
                <h3>Write &amp; Build</h3>
                <div className="v2-pricing-amount">$0 <span>forever</span></div>
                <ul className="v2-pricing-list">
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Unlimited writing &amp; prompts</li>
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Unlimited photo uploads</li>
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Audio transcription</li>
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Digital preview</li>
                </ul>
              </div>
              <div className="v2-pricing-tier">
                <h3>Print Hardcover</h3>
                {sale.saleActive ? (
                  <div className="v2-pricing-amount">
                    <span style={{ textDecoration: 'line-through', opacity: 0.5, fontWeight: 500, marginRight: 10 }}>{fmtUsd(sale.anchorCents)}</span>
                    {fmtUsd(sale.saleCents)} <span>per book</span>
                    <span style={{ display: 'inline-block', marginLeft: 10, verticalAlign: 'middle', fontSize: 14, fontWeight: 700, color: '#2e7d32', background: 'rgba(46,125,50,0.12)', borderRadius: 6, padding: '2px 8px' }}>{SALE_OFFER.tag}</span>
                  </div>
                ) : (
                  <div className="v2-pricing-amount">$89<span style={{ fontSize: 24 }}>.99</span> <span>per book</span></div>
                )}
                <ul className="v2-pricing-list">
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Up to 200 pages included</li>
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Full-color hardcover</li>
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Free shipping in the US</li>
                  <li><CheckCircle weight="duotone" size={16} style={{ color: ink(0.6), flexShrink: 0 }} /> Discounts on multiple copies</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ REVIEWS ══════════ */}
      <section className="v2-section-alt">
        <Reviews title="Loved by families like yours" maxWidth={1040} />
      </section>

      {/* ══════════ QR / VIDEO & VOICE — dark chapter opens here ══════════ */}
      <section className="v2-section-qr">
        <div className="v2-container" style={{ textAlign: 'center' }}>
          <div className="v2-section-header">
            <p className="v2-section-eyebrow" style={{ color: 'rgba(251,248,241,0.6)' }}>Beyond the Page</p>
            <h2 style={{ color: C.parchment }}>A book that speaks</h2>
            <p style={{ color: 'rgba(251,248,241,0.8)', maxWidth: 620 }}>Some stories are meant to be heard. Record a video or voice message, and we'll place a QR code right on the page. When they scan it, they hear you in your own voice.</p>
          </div>
          <div className="v2-qr-grid">
            <div className="v2-qr-card">
              <VideoCamera weight="duotone" size={28} style={{ color: C.parchment, marginBottom: 16 }} />
              <h4>Video messages</h4>
              <p>Record a video right from your phone or laptop. It lives alongside your story in the book. Just scan and watch.</p>
            </div>
            <div className="v2-qr-card">
              <Microphone weight="duotone" size={28} style={{ color: C.parchment, marginBottom: 16 }} />
              <h4>Voice recordings</h4>
              <p>Not a video person? Just talk. Hearing your voice is something your family will come back to again and again.</p>
            </div>
            <div className="v2-qr-card">
              <DeviceMobile weight="duotone" size={28} style={{ color: C.parchment, marginBottom: 16 }} />
              <h4>Scan to play</h4>
              <p>A small QR code prints right on the page. They pick up their phone, point, and hear their family speaking to them.</p>
            </div>
          </div>
          <p className="v2-qr-hook">Imagine them reading your story, then scanning the code and hearing your voice say it.</p>
        </div>
      </section>

      {/* ══════════ FINAL CTA ══════════ */}
      <section className="v2-section-cta">
        <div className="v2-container-narrow">
          <h2 style={{ fontFamily: F.heading, fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 400, marginBottom: 24 }}>Your stories deserve <em style={{ fontStyle: 'italic' }}>a real book.</em></h2>
          <p style={{ fontSize: 20, color: 'rgba(251,248,241,0.8)', marginBottom: 40, maxWidth: 520, margin: '0 auto 40px' }}>Free to start. No credit card. Your first story takes five minutes.</p>
          <button className="v2-btn-primary v2-btn-primary-hero" style={{ background: C.parchment, color: C.ink, boxShadow: SH.lg }} onClick={() => onGetStarted('own_story')}
            onMouseEnter={e => { e.currentTarget.style.background = C.tan; e.currentTarget.style.transform = 'scale(1.02)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = C.parchment; e.currentTarget.style.transform = 'scale(1)'; }}
          >Start Your Book For Free</button>
          <p style={{ fontSize: 14, color: 'rgba(251,248,241,0.5)', marginTop: 16 }}>No credit card required · First story in 5 minutes</p>
          <p style={{ fontSize: 14, color: 'rgba(251,248,241,0.4)', marginTop: 12 }}>Already a member? <button onClick={onSignIn} style={{ background: 'none', border: 'none', color: 'rgba(251,248,241,0.6)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', textDecoration: 'underline' }}>Sign in here.</button></p>

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 32, marginTop: 64, paddingTop: 64, borderTop: '1px solid rgba(251,248,241,0.1)', fontSize: 14, color: 'rgba(251,248,241,0.5)', fontWeight: 500 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><ShieldCheck weight="duotone" size={16} /> AES-256 encrypted</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Lock weight="duotone" size={16} /> No ads, ever</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Lock weight="duotone" size={16} /> Talebound never trains on your stories</div>
          </div>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="v2-footer">
        <div className="v2-footer-inner">
          <div className="v2-footer-brand">
            <span style={{ fontFamily: F.heading, fontSize: '1.4rem', fontWeight: 700, letterSpacing: 1, color: C.ink }}>Talebound</span>
            <span style={{ fontFamily: F.body, fontSize: '0.75rem', color: C.muted, fontStyle: 'italic', letterSpacing: 1 }}>Stories bound to last</span>
          </div>
          <div className="v2-footer-links">
            <a className="v2-footer-link" href="mailto:support@taleboundbook.com">Support</a>
            <a className="v2-footer-link" href="/privacy.html">Privacy</a>
            <a className="v2-footer-link" href="/terms.html">Terms</a>
            <a className="v2-footer-link" href="/security.html">Security</a>
          </div>
          <div className="v2-footer-social">
            <a href="https://www.instagram.com/taleboundbook/" target="_blank" rel="noopener noreferrer" aria-label="Talebound on Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
              </svg>
            </a>
            <a href="https://www.facebook.com/Taleboundbook" target="_blank" rel="noopener noreferrer" aria-label="Talebound on Facebook">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
              </svg>
            </a>
          </div>
          <div className="v2-footer-legal">© 2026 Talebound.</div>
        </div>
      </footer>
    </div>
  );
}
